import React, {useCallback} from 'react';
import I18n from 'app/i18n';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {inject, observer} from 'mobx-react';
import {casinoGamesStore, shopLootboxStore} from 'app/store/global';
import {CASINO_TAB_LOOTBOX} from 'app/utils/Consts';
import {getImageDomain, isDesktop} from 'app/utils';
import {CURRENCY_USD} from 'app/utils/ShopItemPriceFormat';
import {goToShopLootbox} from 'app/utils/app_links';
import {translatedName} from 'app/utils/translations_helper';
import ShopItemInterface from 'app/interfaces/shop/ShopItemInterface';
import BuyButton from 'app/components/shop/lootboxes/buy_button';
import {coolOffLootbox} from 'app/utils/isCoolOffActiveFor';
import {UserStore} from 'app/store/UserStore';
import {useCarousel} from 'app/utils/useCarousel';

interface CasinoLootboxBlockProps {
    lootboxes: ShopItemInterface[];
    onUpdateShop?: () => void;
    scrollToFilter: (forceScroll?: boolean, instantaneous?: boolean) => void;
    style?: React.CSSProperties;
    userStore?: UserStore;
}

const CasinoLootboxBlockComponent = ({
    lootboxes,
    onUpdateShop,
    scrollToFilter,
    style,
    userStore
}: CasinoLootboxBlockProps): React.ReactElement => {
    const itemsCount = lootboxes.length;
    const {activeIndex, handleNextClick, handlePrevClick} = useCarousel(itemsCount);

    const currentLootbox = lootboxes[activeIndex];
    const domain = getImageDomain(false);
    const rawUrl = currentLootbox.image_url ?? currentLootbox.image?.url ?? null;
    const imageUrl = rawUrl ? `${domain}${rawUrl}` : null;
    const user = userStore?.user;
    const confirmTitle = currentLootbox.recipe
        ? translatedName(currentLootbox.recipe)
        : currentLootbox.title;
    const coolOffDisabled = user ? coolOffLootbox(user) : false;

    const afterPurchase = useCallback((): void => {
        const desktop = isDesktop();
        const {recipe} = currentLootbox;

        if (recipe?.slug) {
            if (!desktop) {
                scrollToFilter(true, true);
            }

            shopLootboxStore.setReturnToCasinoAllGamesOnLootboxBack(true);
            shopLootboxStore.updateOpenNowStatus(true);
            casinoGamesStore.changeFilterGroup(CASINO_TAB_LOOTBOX);
            goToShopLootbox(recipe.slug, false, {isCasinoPage: true, preventScroll: true});
            onUpdateShop?.();

            setTimeout(() => {
                scrollToFilter(true, !desktop);
            }, 0);
        }
    }, [
        currentLootbox,
        onUpdateShop,
        scrollToFilter
    ]);

    return (
        <div className="casino-lootbox-block medium-marketing-block marketing-block" style={style}>
            <div className="casino-lootbox-block__header">
                <span className="casino-lootbox-block__title">
                    <SpriteIcon
                        name="#svg-cubes"
                        className="casino-lootbox-block__icon svg-default"
                        size={{height: 20, width: 20}}
                    />
                    {I18n.t('lootboxes')}
                </span>
            </div>
            <div className="casino-lootbox-block__banner-wrap">
                {itemsCount > 1 && <div className="marketing-block-progress">
                    {lootboxes.map((_, i) => {
                        const isCurrent = i === activeIndex;
                        const isActive = i < activeIndex;
                        const segmentClass = `marketing-block-progress__segment ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`;

                        return <span key={i} className={segmentClass} />;
                    })}
                </div>}
                <div className="casino-lootbox-block__banner">
                    <img key={activeIndex} src={imageUrl} alt="Lootbox" className="casino-lootbox-block__banner-img" />
                </div>
                <BuyButton
                    key={`casino-lootbox-block-${activeIndex}`}
                    {...currentLootbox}
                    afterBuy={afterPurchase}
                    currency={CURRENCY_USD}
                    disabled={coolOffDisabled}
                    show_loading={false}
                    title={confirmTitle}
                    type_shop="lootbox"
                    user={user}
                    with_confirmation={true}
                />
            </div>
            {itemsCount > 1 && <>
                <button
                    type="button"
                    className="marketing-block-arrow marketing-block-arrow_prev"
                    onClick={handlePrevClick}
                >
                    <SpriteIcon name="#pag-arr-left" className="svg-default"/>
                </button>
                <button
                    type="button"
                    className="marketing-block-arrow marketing-block-arrow_next"
                    onClick={handleNextClick}
                >
                    <SpriteIcon name="#pag-arr-right" className="svg-default"/>
                </button>
            </>}
        </div>
    );
};

const CasinoLootboxBlock = inject('userStore')(observer(CasinoLootboxBlockComponent));

export default CasinoLootboxBlock;
